import { Injectable } from "@angular/core";
import { AppContextSelector, ProductType } from "@obg/ngx.core";
import { memoizeSelector } from "@obg/ngx.global";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { RouteLink } from "@obg/ngx.router";
import { camelCase, get } from "lodash-es";

import { GameSelector } from "../game/game.selector";
import { GameCollectionSelector } from "../game-collection/game-collection.selector";
import { GAMING_ROUTE_MAP, GamingRoutes } from "../route";
import { GamingExternalState } from "../state";

export const PRODUCT_MAPPING: Record<string, ProductType> = {
	Casino: ProductType.Casino,
	MiniGames: ProductType.MiniGames,
	TableGames: ProductType.TableGames,
	Keno: ProductType.Keno,
	LiveCasino: ProductType.LiveCasino,
	PokerLobby: ProductType.Poker,
	TurkishPoker: ProductType.TurkishPoker,
	Sportsbook: ProductType.VirtualSports,
	Slots: ProductType.Slots,
	SkillGames: ProductType.SkillGames,
};

// @dynamic
@Injectable({
	providedIn: "root",
})
export class GameRouteSelector {
	private logger: Log;

	constructor(
		private gameSelector: GameSelector,
		private gameCollectionSelector: GameCollectionSelector,
		logging: LoggingFactory
	) {
		this.logger = logging.get("gameRouteSelector");
	}

	@memoizeSelector<GamingExternalState>(undefined, () => [AppContextSelector.getProduct])
	getRouteNameByProduct(routeName: GamingRoutes, product?: ProductType) {
		return (state: GamingExternalState): string | undefined => {
			const currentProduct = product || AppContextSelector.getProduct(state);
			const route = currentProduct ? get(GAMING_ROUTE_MAP[currentProduct], camelCase(routeName)) : undefined;

			if (!route) {
				this.logger.warn("getRouteNameByProduct", "route not found", { routeName });
			}
			return route;
		};
	}

	@memoizeSelector(undefined, () => [
		GameCollectionSelector.prototype.getById,
		GameCollectionSelector.prototype.getRootIdById,
		GameRouteSelector.prototype.getRouteNameByProduct,
		GameSelector.prototype.getById,
	])
	getRouteLinkByGameId(gameId: string, routeName: GamingRoutes, queryParams?: Record<string, any>) {
		return (state: GamingExternalState): RouteLink | undefined => {
			const game = this.gameSelector.getById(gameId)(state) || this.gameSelector.getDetailById(gameId)(state);

			if (!game) {
				return;
			}

			const { collections, primaryType, slug } = game;
			const gameCollectionId =
				this.gameCollectionSelector.getRootIdById(primaryType)(state) ||
				collections.find((collection) => Object.keys(PRODUCT_MAPPING).includes(collection));
			const gameRouteProduct = gameCollectionId ? PRODUCT_MAPPING[gameCollectionId] : undefined;
			const route = this.getRouteNameByProduct(routeName, gameRouteProduct)(state);
			const gameCollection = this.gameCollectionSelector.getById(primaryType)(state);

			return {
				route,
				params: {
					game: slug,
					category: gameCollection?.slug,
				},
				extras: {
					queryParams,
				},
			};
		};
	}
}
